import { firstOccurrenceFrom, nextOccurrence, type RecurrenceRuleFields } from './recurrence';

// Compostos legados → [unidade base, passo] (defesa para dados antigos).
const legacyCompoundSteps: Record<string, [string, number]> = {
    biweekly: ['weekly', 2],
    bimonthly: ['monthly', 2],
    quarterly: ['monthly', 3],
    semiannual: ['monthly', 6],
};

export function toMonthlyRecurringAmount(
    amount: number,
    frequency: string,
    interval = 1,
    weekdaysCount = 1,
): number {
    const [unit, step] = legacyCompoundSteps[frequency] ?? [frequency, interval];
    // Semanal multi-dia: N cobranças por semana.
    const perWeek = unit === 'weekly' ? Math.max(1, weekdaysCount) : 1;
    switch (unit) {
        case 'daily': return (amount * 30) / step;
        case 'weekly': return (amount * 4.33 * perWeek) / step;
        case 'monthly': return amount / step;
        case 'annual': return amount / (12 * step);
        default: return amount / step;
    }
}

/**
 * Equivalente mensal de uma regra de recorrência, lendo `interval` e `weekdays` da própria
 * linha. Existe para que ninguém precise lembrar de passar os dois: esquecê-los faz uma
 * quinzenal (weekly + interval 2) ser contada como semanal — o dobro do valor real.
 */
export function monthlyAmountOfRule(rule: {
    amount: number;
    frequency: string;
    interval?: number | null;
    weekdays?: string | null;
}): number {
    return toMonthlyRecurringAmount(
        rule.amount,
        rule.frequency,
        rule.interval ?? 1,
        rule.weekdays ? rule.weekdays.split(',').filter(Boolean).length : 1,
    );
}

export function getOccurrencesForMonth(
    rule: RecurrenceRuleFields,
    monthStart: string,
    monthEnd: string,
    endDate?: string | null,
    skipped?: Set<string>,
): string[] {
    if (rule.startDate > monthEnd) return [];

    const dates: string[] = [];
    let current = firstOccurrenceFrom(monthStart, rule);
    let guard = 1000;

    while (current <= monthEnd && guard > 0) {
        guard -= 1;
        // Exceção por exclusão: slots pulados não aparecem no calendário de assinaturas.
        if ((!endDate || current <= endDate) && !skipped?.has(current)) {
            dates.push(current);
        }
        const next = nextOccurrence(current, rule);
        if (next <= current) break;
        current = next;
    }

    return dates;
}
